import React from 'react';
import {
  FormControl,
  FormControlLabel,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTokens } from '@mcn-platform/models';
import { splitSeparator } from 'constants/general';
import { StandardFieldProps } from './FieldTypes';
import BulletPoints from './bulletItemsInput/BulletItems';
import HelperText from './HelperText';
import GenerateAIDialog from './GenerateAIDialog';
import GenerateAiFormLabel from './GenerateAiFormLabel';

const promptList = [
  'Write three short learning objectives for this course.',
];
const formatMessage = 'Return the output with no bullet points or numbers, and separate each learning objective by a newline character.';

const StandardBulletsPointsField = ({
  control,
  fieldName,
  field,
  readOnly,
}: StandardFieldProps) => {
  const [aiDialogOpen, setAiDialogOpen] = React.useState(false);
  const tokens = useTokens();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({
        field: { onChange, value },
        fieldState: { isTouched, error },
      }) => {
        const handleGenerateFieldCancel = () => {
          setAiDialogOpen(false);
        };

        const handleGenerateFieldConfirm = (newValue: string | string[]) => {
          setAiDialogOpen(false);
          console.log(newValue);

          let parsedNewValue: string[];

          if (Array.isArray(newValue)) {
            parsedNewValue = newValue;
            } else {
              try {
                const parsed = JSON.parse(newValue);
                if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
                  parsedNewValue = parsed;
                } else {
                  throw new Error('Invalid JSON format');
                }
              } catch (err) {
                console.warn('JSON parse error:', err);

                parsedNewValue = newValue
                  .split('\n')
                  .map((item) => item.replace(/^[-*•\d.\s]+/, '').trim())
                  .filter((item) => !!item);
              }
            }

            onChange(parsedNewValue.join(splitSeparator));
        };

        // eslint-disable-next-line no-underscore-dangle
        const { key } = control._formValues;
        const label = field.tokenKey ? tokens.getToken(field.tokenKey) : field.label || 'No Label';

        const visiblePrompts = field.promptList || promptList;
        const promptsForAI = visiblePrompts.map((p) => `${p} ${formatMessage}`);

        return (
          <FormControl fullWidth>
            <FormControlLabel
              label={
                !readOnly && field.aiTarget ? (
                  <GenerateAiFormLabel
                    label={label}
                    onClick={() => setAiDialogOpen(true)}
                  />
                ) : label
              }
              disabled={field.readOnly || readOnly}
              labelPlacement="top"
              control={(
                <BulletPoints
                  value={value}
                  setValue={onChange}
                  placeholder={field.placeholder || ''}
                  disabled={Boolean(field.readOnly || readOnly)}
                />
              )}
            />
            <HelperText
              name={fieldName}
              error={isTouched && error?.message}
              description={field.description}
            />
            {field?.aiTarget && (
              <GenerateAIDialog
                open={aiDialogOpen}
                title="Generate AI"
                promptList={promptsForAI}
                itemType={field.aiTarget}
                itemKey={key}
                onConfirm={handleGenerateFieldConfirm}
                onCancel={handleGenerateFieldCancel}
                onClose={() => setAiDialogOpen(false)}
                aiContentType="bulletPoints"
              />
            )}
          </FormControl>
        );
      }}
    />
  );
};

StandardBulletsPointsField.defaultProps = {
  description: undefined,
};

export default StandardBulletsPointsField;
